import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Trophy, Users, Ticket, Gift, Plus, LogOut, PartyPopper, Trash2, KeyRound, Copy, Check, UserPlus, Pencil, X, BarChart3, ScanLine, History, LayoutDashboard, Search, BookOpen, Link2, MessageCircle, GitMerge } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AppShell, IconButton } from '../components/layout/AppShell';
import { Card, EmptyState, SectionHeader } from '../components/layout/Primitives';
import CreateDrawForm from '../components/CreateDrawForm';
import DistributeTicketsForm from '../components/DistributeTicketsForm';
import ParticipantsTable from '../components/ParticipantsTable';
import DrawsTable from '../components/DrawsTable';
import StatsCard from '../components/StatsCard';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import PollsManager from '../components/PollsManager';
import QRScannerModal from '../components/QRScannerModal';
import ScanHistory from '../components/ScanHistory';
import MenusManager from '../components/menu/MenusManager';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PURPLE = '#A855F7';

const AdminDashboard = ({ admin, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [draws, setDraws] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersWithStats, setUsersWithStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'overview');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingNickname, setEditingNickname] = useState(null);
  const [nicknameInput, setNicknameInput] = useState('');
  const [showArchivedDraws, setShowArchivedDraws] = useState(false);
  const [acknowledgedUsers, setAcknowledgedUsers] = useState(() => {
    const saved = localStorage.getItem('acknowledgedUsers');
    return saved ? JSON.parse(saved) : [];
  });
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [referralBonus, setReferralBonus] = useState(0);
  const [referralBonusInput, setReferralBonusInput] = useState('');
  const [savingBonus, setSavingBonus] = useState(false);
  const [prizeText, setPrizeText] = useState('');
  const [savingPrizeText, setSavingPrizeText] = useState(false);
  const [accessCodeSet, setAccessCodeSet] = useState(false);
  const [newAccessCode, setNewAccessCode] = useState('');
  const [savingAccessCode, setSavingAccessCode] = useState(false);
  const [copiedInvite, setCopiedInvite] = useState('');
  const [referralUser, setReferralUser] = useState(null);
  const [referrerChoice, setReferrerChoice] = useState('');
  const [bubbleChoice, setBubbleChoice] = useState(null);
  const [refSearch, setRefSearch] = useState('');
  const [savingReferral, setSavingReferral] = useState(false);
  const [savingFriendId, setSavingFriendId] = useState(null);
  const [mergeUser, setMergeUser] = useState(null);
  const [mergeDuplicateId, setMergeDuplicateId] = useState('');
  const [mergeSearch, setMergeSearch] = useState('');
  const [merging, setMerging] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [editingNumbers, setEditingNumbers] = useState(null);
  const [editPrimary, setEditPrimary] = useState('');
  const [editAdditional, setEditAdditional] = useState([]);
  const [showAdminScanner, setShowAdminScanner] = useState(false);
  const [newFeedbackCount, setNewFeedbackCount] = useState(0);

  const token = localStorage.getItem('token');

  // Check if a user is "new" (last 24h and not acknowledged)
  const isNewUser = (user) => {
    return !user.nickname && !acknowledgedUsers.includes(user.id);
  };

  // Filter and sort users based on search query and sort column
  const filteredUsers = users
    .filter(user => {
      const query = searchQuery.toLowerCase();
      return (
        user.name?.toLowerCase().includes(query) ||
        user.nickname?.toLowerCase().includes(query) ||
        user.whatsapp_number?.includes(query)
      );
    })
    .sort((a, b) => {
      // New (unacknowledged) users always float to top
      const aNew = isNewUser(a) ? 0 : 1;
      const bNew = isNewUser(b) ? 0 : 1;
      if (aNew !== bNew) return aNew - bNew;

      if (!sortColumn) return 0;
      
      let aVal, bVal;
      if (sortColumn === 'tickets') {
        aVal = a.total_tickets || 0;
        bVal = b.total_tickets || 0;
      } else if (sortColumn === 'wins') {
        aVal = a.times_won || 0;
        bVal = b.times_won || 0;
      } else {
        return 0;
      }
      
      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
    });

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle between desc and asc
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      // New column - start with descending (highest first)
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Calculate recent signups (last 24 hours) excluding acknowledged ones
  const recentUsers = users.filter(user => isNewUser(user));

  const newSignupsCount = recentUsers.length;

  const acknowledgeUser = (userId) => {
    const updated = [...acknowledgedUsers, userId];
    setAcknowledgedUsers(updated);
    localStorage.setItem('acknowledgedUsers', JSON.stringify(updated));
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch stats');
    }
  };

  const fetchReferralBonus = async () => {
    try {
      const response = await axios.get(`${API}/admin/referral-bonus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralBonus(response.data.referral_bonus || 0);
      setReferralBonusInput(response.data.referral_bonus?.toString() || '0');
      setPrizeText(response.data.referral_prize_text || '');
    } catch (error) {
      console.error('Failed to fetch referral bonus:', error);
    }
  };

  const updateReferralBonus = async () => {
    const amount = parseFloat(referralBonusInput) || 0;
    if (amount < 0) {
      toast.error('Bonus amount cannot be negative');
      return;
    }
    
    setSavingBonus(true);
    try {
      const response = await axios.post(
        `${API}/admin/set-referral-bonus`,
        { amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReferralBonus(amount);
      toast.success(response.data.message);
      fetchDraws(); // Refresh draws to show updated bonus
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bonus');
    } finally {
      setSavingBonus(false);
    }
  };

  const savePrizeText = async () => {
    if (!prizeText.trim()) {
      toast.error('Prize text cannot be empty');
      return;
    }
    setSavingPrizeText(true);
    try {
      await axios.post(
        `${API}/admin/set-referral-prize-text`,
        { text: prizeText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Referral prize text updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update prize text');
    } finally {
      setSavingPrizeText(false);
    }
  };


  const fetchAccessCodeStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/access-code-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccessCodeSet(!!response.data.is_set);
    } catch (error) {
      console.error('Failed to fetch access code status:', error);
    }
  };

  const saveAccessCode = async () => {
    const code = newAccessCode.trim();
    if (code.length < 4) {
      toast.error('Access code must be at least 4 characters');
      return;
    }
    setSavingAccessCode(true);
    try {
      await axios.post(
        `${API}/admin/set-access-code`,
        { code },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Members club access code updated');
      setNewAccessCode('');
      setAccessCodeSet(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update access code');
    } finally {
      setSavingAccessCode(false);
    }
  };

  const inviteUrl = `${window.location.origin}/join`;

  const copyInvite = (text, which) => {
    navigator.clipboard.writeText(text);
    setCopiedInvite(which);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopiedInvite(''), 2000);
  };

  const shareInviteWhatsApp = (link) => {
    const msg = `Join our members club and get your raffle tickets! Sign up here: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const fetchDraws = async () => {
    try {
      const response = await axios.get(`${API}/admin/draws`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_archived: showArchivedDraws }
      });
      setDraws(response.data);
    } catch (error) {
      toast.error('Failed to fetch draws');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Sort users alphabetically by name
      const sortedUsers = response.data.sort((a, b) => 
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
      setUsers(sortedUsers);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchFeedbackCount = async () => {
    try {
      const res = await axios.get(`${API}/admin/polls/feedback/unseen-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewFeedbackCount(res.data.count || 0);
    } catch (error) {
      // silent — badge just stays as-is
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchDraws(), fetchUsers(), fetchReferralBonus(), fetchAccessCodeStatus(), fetchFeedbackCount()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Refetch draws when archive filter changes
  useEffect(() => {
    fetchDraws();
  }, [showArchivedDraws]);

  // Keep the poll-feedback alert badge fresh when opening the Polls tab
  useEffect(() => {
    if (activeTab === 'polls') fetchFeedbackCount();
    // eslint-disable-next-line
  }, [activeTab]);

  const handleDrawCreated = () => {
    fetchDraws();
    fetchStats();
    setActiveTab('draws');
  };

  const handleTicketsDistributed = () => {
    fetchStats();
    fetchUsers(); // Refresh users to update active ticket counts
  };

  const handleSaveNickname = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/users/${userId}/nickname`,
        { nickname: nicknameInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Nickname updated successfully');
      acknowledgeUser(userId);
      setEditingNickname(null);
      setNicknameInput('');
      fetchUsers(); // Refresh users list
    } catch (error) {
      console.error('Nickname update error:', error);
      toast.error('Failed to update nickname');
    }
  };

  const generateTempPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const openResetPasswordModal = (user) => {
    const tempPassword = generateTempPassword();
    setResetPasswordUser(user);
    setGeneratedPassword(tempPassword);
    setShowResetModal(true);
    setCopiedPassword(false);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser) return;
    
    setResettingPassword(true);
    try {
      await axios.put(
        `${API}/admin/users/${resetPasswordUser.id}/reset-password`,
        { new_password: generatedPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password reset for ${resetPasswordUser.name}. Share the temporary password with them.`);
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
      setShowResetModal(false);
    } finally {
      setResettingPassword(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    setCopiedPassword(true);
    toast.success('Password copied to clipboard!');
    setTimeout(() => setCopiedPassword(false), 2000);
  };


  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const deleteUser = async () => {
    if (!userToDelete) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/admin/users/${userToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User deleted successfully');
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchUsers();
      fetchStats();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const startEditingNumbers = (user) => {
    setEditingNumbers(user.id);
    setEditPrimary(user.whatsapp_number);
    setEditAdditional(user.additional_numbers ? [...user.additional_numbers] : []);
  };

  const saveNumbers = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/users/${userId}/numbers`,
        { whatsapp_number: editPrimary, additional_numbers: editAdditional.filter(n => n.trim()) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Numbers updated');
      setEditingNumbers(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update numbers');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#09090b]">
        <div className="animate-pulse font-heading text-sm uppercase tracking-widest text-[#A855F7]">Loading admin panel</div>
      </div>
    );
  }

  const tabs = [
    { value: 'overview', label: 'Overview', icon: LayoutDashboard },
    { value: 'draws', label: 'Draws', icon: Gift },
    { value: 'create', label: 'Create Draw', icon: Plus },
    { value: 'distribute', label: 'Distribute Tickets', icon: Ticket },
    { value: 'users', label: 'Users', icon: Users, badge: newSignupsCount },
    { value: 'invite', label: 'Invite', icon: Link2 },
    { value: 'menus', label: 'Menus', icon: BookOpen },
    { value: 'polls', label: 'Polls', icon: BarChart3, badge: newFeedbackCount },
    { value: 'scanlog', label: 'Scan Log', icon: History },
    { value: 'scan', label: 'Scan', icon: ScanLine, desktop: false, action: () => setShowAdminScanner(true) },
  ];

  const pageMeta = {
    overview: { title: 'Overview', subtitle: 'Everything at a glance' },
    invite: { title: 'Invite Members', subtitle: 'Share a link to your sign-up page' },
    users: { title: 'Users', subtitle: `${users.length} member${users.length === 1 ? '' : 's'} · tap a field to edit` },
  }[activeTab] || {};

  const isRecent = (user) => recentUsers.some((u) => u.id === user.id);

  const renderNickname = (user) => (
    editingNickname === user.id ? (
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={nicknameInput}
          onChange={(e) => setNicknameInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSaveNickname(user.id);
            if (e.key === 'Escape') { setEditingNickname(null); setNicknameInput(''); }
          }}
          placeholder="Add nickname…"
          className="w-32 rounded-lg border border-[#A855F7] bg-[#09090b] px-2 py-1 text-sm text-white focus:outline-none"
          autoFocus
          data-testid={`nickname-input-${user.id}`}
        />
        <button onClick={() => handleSaveNickname(user.id)} className="text-[#10b981]" aria-label="Save"><Check className="h-4 w-4" /></button>
        <button onClick={() => { setEditingNickname(null); setNicknameInput(''); }} className="text-[#f43f5e]" aria-label="Cancel"><X className="h-4 w-4" /></button>
      </div>
    ) : (
      <button
        type="button"
        onClick={() => { setEditingNickname(user.id); setNicknameInput(user.nickname || ''); }}
        className="tap-none group inline-flex items-center gap-1.5 text-left"
        data-testid={`edit-nickname-trigger-${user.id}`}
      >
        {user.nickname ? (
          <span className="text-sm font-medium text-[#EAB308]">{user.nickname}</span>
        ) : (
          <span className="text-sm text-[#52525b]">Add nickname…</span>
        )}
        <Pencil className="h-3 w-3 text-[#52525b] md:opacity-0 md:group-hover:opacity-100" />
      </button>
    )
  );

  const renderNumbers = (user) => (
    editingNumbers === user.id ? (
      <div className="min-w-[180px] space-y-1.5">
        <input
          type="text"
          value={editPrimary}
          onChange={(e) => setEditPrimary(e.target.value)}
          className="w-full rounded-lg border border-white/[0.1] bg-[#09090b] px-2 py-1.5 text-xs text-white outline-none focus:border-[#A855F7]"
          data-testid={`edit-primary-number-${user.id}`}
        />
        {editAdditional.map((num, idx) => (
          <div key={idx} className="flex items-center gap-1">
            <input
              type="text"
              value={num}
              onChange={(e) => { const updated = [...editAdditional]; updated[idx] = e.target.value; setEditAdditional(updated); }}
              className="flex-1 rounded-lg border border-white/[0.1] bg-[#09090b] px-2 py-1.5 text-xs text-[#A855F7] outline-none focus:border-[#A855F7]"
            />
            <button onClick={() => setEditAdditional(editAdditional.filter((_, i) => i !== idx))} className="p-1 text-red-400" data-testid={`delete-additional-${idx}`}>
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <div className="mt-1 flex gap-1.5">
          <button onClick={() => saveNumbers(user.id)} className="rounded-md bg-[#22c55e] px-2.5 py-1 text-xs font-semibold text-black" data-testid={`save-numbers-${user.id}`}>Save</button>
          <button onClick={() => setEditAdditional([...editAdditional, ''])} className="rounded-md bg-[#A855F7] px-2.5 py-1 text-xs font-semibold text-white">+ Add</button>
          <button onClick={() => setEditingNumbers(null)} className="px-2 py-1 text-xs text-[#a1a1aa]">Cancel</button>
        </div>
      </div>
    ) : (
      <button
        type="button"
        className="tap-none group text-left"
        onClick={() => startEditingNumbers(user)}
        data-testid={`edit-numbers-trigger-${user.id}`}
      >
        <div className="flex items-center gap-1.5 text-sm text-[#a1a1aa]">
          {user.whatsapp_number}
          <Pencil className="h-3 w-3 text-[#52525b] md:opacity-0 md:group-hover:opacity-100" />
        </div>
        {user.additional_numbers?.length > 0 && (
          <div className="mt-0.5 text-xs text-[#A855F7]">{user.additional_numbers.join(', ')}</div>
        )}
      </button>
    )
  );

  const openReferralModal = (user) => {
    setReferralUser(user);
    setReferrerChoice(user.referred_by || '');
    setBubbleChoice(typeof user.referral_bubbles === 'number' ? user.referral_bubbles : 0);
    setRefSearch('');
  };

  const setFriendBubbles = async (friendId, n) => {
    setSavingFriendId(friendId);
    try {
      await axios.post(`${API}/admin/users/${friendId}/set-bubbles`, { bubbles: n }, { headers: { Authorization: `Bearer ${token}` } });
      setUsers((prev) => prev.map((u) => (u.id === friendId ? { ...u, referral_bubbles: n } : u)));
      toast.success('Friend’s bubbles updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update bubbles');
    } finally {
      setSavingFriendId(null);
    }
  };

  const saveReferral = async () => {
    if (!referralUser) return;
    setSavingReferral(true);
    try {
      await axios.post(`${API}/admin/users/${referralUser.id}/set-referrer`, { referrer_id: referrerChoice || null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Referral details updated');
      setReferralUser(null);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingReferral(false);
    }
  };

  const openMergeModal = (user) => {
    setMergeUser(user);
    const firstMatch = user.duplicate_matches?.[0];
    setMergeDuplicateId(firstMatch?.id || '');
    setMergeSearch('');
  };

  const doMerge = async () => {
    if (!mergeDuplicateId) { toast.error('Pick the duplicate account to merge in'); return; }
    setMerging(true);
    try {
      const res = await axios.post(`${API}/admin/users/merge`, { primary_id: mergeUser.id, duplicate_id: mergeDuplicateId }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || 'Members merged');
      setMergeUser(null);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const renderUserActions = (user) => (
    <div className="flex items-center gap-2">
      <Button
        data-testid={`edit-referral-btn-${user.id}`}
        onClick={(e) => { e.stopPropagation(); openReferralModal(user); }}
        variant="outline"
        size="sm"
        className="h-9 w-9 rounded-full border-[#10b981]/40 p-0 text-[#10b981] hover:bg-[#10b981] hover:text-black"
        title="Introduced by / referral progress"
      >
        <UserPlus className="w-4 h-4" />
      </Button>
      <Button
        data-testid={`merge-user-btn-${user.id}`}
        onClick={(e) => { e.stopPropagation(); openMergeModal(user); }}
        variant="outline"
        size="sm"
        className={`h-9 w-9 rounded-full border-[#A855F7]/40 p-0 text-[#A855F7] hover:bg-[#A855F7] hover:text-white ${user.possible_duplicate ? 'ring-2 ring-[#f59e0b] ring-offset-2 ring-offset-[#18181b]' : ''}`}
        title={user.possible_duplicate ? `Possible duplicate (${user.duplicate_reason}) — merge` : 'Merge a duplicate into this member'}
      >
        <GitMerge className="w-4 h-4" />
      </Button>
      <Button
        data-testid={`delete-user-btn-${user.id}`}
        onClick={(e) => { e.stopPropagation(); openDeleteDialog(user); }}
        variant="outline"
        size="sm"
        className="h-9 w-9 rounded-full border-[#f43f5e]/40 p-0 text-[#f43f5e] hover:bg-[#f43f5e] hover:text-white"
        title="Delete User"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );

  const NewBadge = ({ user }) => (
    <button
      onClick={(e) => { e.stopPropagation(); acknowledgeUser(user.id); }}
      className="rounded-full bg-[#10b981] px-2 py-0.5 text-[10px] font-bold text-black"
      title="Dismiss"
      data-testid={`ack-user-${user.id}`}
    >
      NEW
    </button>
  );

  const DupBadge = ({ user }) => (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-[#f59e0b]/15 px-2 py-0.5 text-[10px] font-bold text-[#f59e0b]"
      title={`Possible duplicate — ${user.duplicate_reason}`}
      data-testid={`dup-badge-${user.id}`}
    >
      <GitMerge className="h-3 w-3" /> Possible duplicate
    </span>
  );

  const SortHeader = ({ col, label, color }) => (
    <th
      className="cursor-pointer px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#a1a1aa] transition-colors hover:text-white"
      onClick={() => handleSort(col)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortColumn === col && <span style={{ color }}>{sortDirection === 'desc' ? '↓' : '↑'}</span>}
      </div>
    </th>
  );

  return (
    <>
      <AppShell
        accent={PURPLE}
        accentFg="text-white"
        tabs={tabs}
        mobileTabValues={['overview', 'draws', 'users', 'scan']}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        pageTitle={pageMeta.title}
        pageSubtitle={pageMeta.subtitle}
        headerLeft={
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#A855F7]/15">
              <Trophy className="h-4 w-4 text-[#A855F7]" />
            </div>
            <div className="leading-tight">
              <p className="font-heading text-sm font-semibold text-white">Admin</p>
              <p className="text-[10px] font-medium uppercase tracking-widest text-[#71717a]">Raffles Я Us</p>
            </div>
          </div>
        }
        headerRight={
          <>
            <Button
              data-testid="admin-scan-qr-btn"
              onClick={() => setShowAdminScanner(true)}
              className="hidden h-10 rounded-full bg-[#EAB308] px-4 font-semibold text-black hover:bg-[#CA8A04] md:inline-flex"
            >
              <ScanLine className="mr-2 h-4 w-4" />
              Scan QR
            </Button>
            <IconButton data-testid="admin-logout-btn" onClick={onLogout} aria-label="Log out">
              <LogOut className="h-4 w-4" />
            </IconButton>
          </>
        }
      >
        {activeTab === 'overview' && (
          <div data-testid="overview-content" className="space-y-6">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
              <StatsCard icon={Users} label="Total Users" value={stats?.total_users || 0} color="#06b6d4" delay={0} />
              <StatsCard icon={Gift} label="Total Draws" value={stats?.total_draws || 0} color="#EAB308" delay={0.05} />
              <StatsCard icon={Ticket} label="Tickets Distributed" value={stats?.total_tickets || 0} color="#A855F7" delay={0.1} />
              <StatsCard icon={Trophy} label="Prizes Won" value={stats?.prizes_won || 0} color="#EAB308" delay={0.15} />
              <StatsCard icon={PartyPopper} label="Pending Draws" value={stats?.pending_draws || 0} color="#f43f5e" delay={0.2} className="col-span-2 lg:col-span-1" />
            </div>

            <Card className="overflow-hidden">
              <div className="flex items-center gap-3 border-b border-white/[0.06] px-5 py-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#10b981]/15">
                  <UserPlus className="h-4 w-4 text-[#10b981]" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-heading text-[15px] font-semibold text-white">Introduce a Friend bonus</h2>
                  <p className="text-xs text-[#a1a1aa]">Added to every pending draw's prize pot</p>
                </div>
              </div>
              <div className="space-y-4 p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="flex items-center gap-2 rounded-xl border border-white/[0.08] bg-[#09090b] px-4 sm:w-48">
                    <span className="font-heading text-xl font-semibold text-[#71717a]">£</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={referralBonusInput}
                      onChange={(e) => setReferralBonusInput(e.target.value)}
                      data-testid="referral-bonus-input"
                      className="h-12 w-full bg-transparent font-heading text-xl font-semibold text-[#10b981] tabular focus:outline-none"
                    />
                  </div>
                  <Button
                    onClick={updateReferralBonus}
                    disabled={savingBonus}
                    data-testid="save-referral-bonus-btn"
                    className="h-12 rounded-full bg-[#10b981] px-6 font-semibold text-black hover:bg-[#059669]"
                  >
                    {savingBonus ? 'Saving…' : 'Update all draws'}
                  </Button>
                </div>
                <p className="text-xs leading-relaxed text-[#71717a]">
                  Shown to members as a highlighted bonus on their tickets, e.g. "£100 <span className="text-[#10b981]">+ £{referralBonus || 0}</span>".
                </p>

                <div className="border-t border-white/[0.06] pt-4">
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-[#a1a1aa]">Referral reward text</label>
                  <p className="mb-2 mt-0.5 text-xs text-[#71717a]">Shown when a member redeems a full 5-bubble referral.</p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      type="text"
                      value={prizeText}
                      onChange={(e) => setPrizeText(e.target.value)}
                      placeholder="e.g. Free entry to next draw!"
                      data-testid="referral-prize-text-input"
                      className="h-12 flex-1 rounded-xl border border-white/[0.08] bg-[#09090b] px-4 text-sm text-white placeholder:text-[#52525b] focus:border-[#A855F7] focus:outline-none"
                    />
                    <Button
                      onClick={savePrizeText}
                      disabled={savingPrizeText}
                      data-testid="save-prize-text-btn"
                      className="h-12 rounded-full bg-[#A855F7] px-6 font-semibold text-white hover:bg-[#9333EA]"
                    >
                      {savingPrizeText ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="overflow-hidden">
              <div className="flex items-center gap-3 border-b border-white/[0.06] px-5 py-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAB308]/15">
                  <KeyRound className="h-4 w-4 text-[#EAB308]" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="font-heading text-[15px] font-semibold text-white">Members club access code</h2>
                  <p className="text-xs text-[#a1a1aa]">The shared code members enter with their number to log in</p>
                </div>
                <span
                  data-testid="access-code-status"
                  className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${accessCodeSet ? 'bg-[#10b981]/15 text-[#10b981]' : 'bg-[#f43f5e]/15 text-[#f43f5e]'}`}
                >
                  {accessCodeSet ? 'Code active' : 'No code set'}
                </span>
              </div>
              <div className="space-y-3 p-5">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    type="text"
                    autoComplete="off"
                    value={newAccessCode}
                    onChange={(e) => setNewAccessCode(e.target.value)}
                    placeholder="Enter a new access code"
                    data-testid="access-code-input"
                    className="h-12 flex-1 rounded-xl border border-white/[0.08] bg-[#09090b] px-4 text-sm text-white placeholder:text-[#52525b] focus:border-[#EAB308] focus:outline-none"
                  />
                  <Button
                    onClick={saveAccessCode}
                    disabled={savingAccessCode}
                    data-testid="save-access-code-btn"
                    className="h-12 rounded-full bg-[#EAB308] px-6 font-semibold text-black hover:bg-[#CA8A04]"
                  >
                    {savingAccessCode ? 'Saving…' : accessCodeSet ? 'Change code' : 'Set code'}
                  </Button>
                </div>
                <p className="text-xs leading-relaxed text-[#71717a]">
                  Share this code with your members — everyone logs in with their phone number and this code. For security the current code is never shown; setting a new one replaces it instantly.
                </p>
              </div>
            </Card>

            {recentUsers.length > 0 && (
              <Card className="overflow-hidden">
                <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#10b981]/15">
                      <Users className="h-4 w-4 text-[#10b981]" />
                    </div>
                    <div>
                      <h2 className="font-heading text-[15px] font-semibold text-white">Recent signups</h2>
                      <p className="text-xs text-[#a1a1aa]">Tap a row to dismiss</p>
                    </div>
                  </div>
                  <span className="rounded-full bg-[#10b981] px-2.5 py-0.5 text-xs font-bold text-black">{recentUsers.length} new</span>
                </div>
                <div className="divide-y divide-white/[0.06]">
                  {recentUsers.slice(0, 5).map((user) => (
                    <div
                      key={user.id}
                      onClick={() => acknowledgeUser(user.id)}
                      className="tap-none flex cursor-pointer items-center gap-3 px-5 py-3.5 transition-colors active:bg-white/[0.03] hover:bg-white/[0.02]"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#10b981]/15 font-heading text-xs font-semibold text-[#10b981]">
                        {(user.name || '?').slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-white">{user.name}</p>
                        <p className="truncate text-xs text-[#a1a1aa]" onClick={(e) => { e.stopPropagation(); startEditingNumbers(user); setActiveTab('users'); }}>
                          {user.whatsapp_number}
                          {user.additional_numbers?.length > 0 && <span className="ml-1 text-[#A855F7]">+{user.additional_numbers.length}</span>}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs text-[#71717a]">
                        {new Date(user.created_at).toLocaleString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  ))}
                </div>
                {recentUsers.length > 5 && (
                  <button onClick={() => setActiveTab('users')} className="w-full border-t border-white/[0.06] py-3 text-sm font-medium text-[#A855F7]">
                    View all {recentUsers.length} new users →
                  </button>
                )}
              </Card>
            )}

            <div>
              <SectionHeader overline="Latest" title="Recent draws" />
              <DrawsTable draws={draws.slice(0, 5)} onRefresh={fetchDraws} showArchived={showArchivedDraws} onToggleArchived={() => setShowArchivedDraws(!showArchivedDraws)} />
            </div>
          </div>
        )}

        {activeTab === 'draws' && (
          <div data-testid="draws-content">
            <DrawsTable draws={draws} onRefresh={fetchDraws} showArchived={showArchivedDraws} onToggleArchived={() => setShowArchivedDraws(!showArchivedDraws)} />
          </div>
        )}

        {activeTab === 'create' && (
          <div data-testid="create-draw-content">
            <CreateDrawForm onDrawCreated={handleDrawCreated} />
          </div>
        )}

        {activeTab === 'distribute' && (
          <div data-testid="distribute-tickets-content">
            <DistributeTicketsForm users={users} draws={draws.filter((d) => d.status === 'pending')} onTicketsDistributed={handleTicketsDistributed} />
          </div>
        )}

        {activeTab === 'users' && (
          <div data-testid="users-content" className="space-y-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#52525b]" />
              <input
                type="search"
                placeholder="Search name, nickname or number"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="user-search-input"
                className="h-12 w-full rounded-full border border-white/[0.08] bg-[#18181b] pl-11 pr-4 text-sm text-white placeholder:text-[#52525b] focus:border-[#A855F7] focus:outline-none"
              />
            </div>

            {/* Mobile: card list */}
            <div className="space-y-2.5 md:hidden" data-testid="users-mobile-list">
              {filteredUsers.map((user) => (
                <Card key={user.id} className="p-4" data-testid={`user-card-${user.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="truncate text-[15px] font-semibold text-white">{user.name}</p>
                        {isRecent(user) && <NewBadge user={user} />}
                        {user.possible_duplicate && <DupBadge user={user} />}
                      </div>
                      <div className="mt-0.5">{renderNickname(user)}</div>
                    </div>
                    {renderUserActions(user)}
                  </div>
                  <div className="mt-3 border-t border-white/[0.06] pt-3">{renderNumbers(user)}</div>
                  <div className="mt-3 flex items-center gap-4 text-xs text-[#a1a1aa]">
                    <span className="inline-flex items-center gap-1.5"><Ticket className="h-3.5 w-3.5 text-[#A855F7]" /><span className="font-semibold text-white tabular">{user.total_tickets || 0}</span> tickets</span>
                    <span className="inline-flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5 text-[#EAB308]" /><span className="font-semibold text-white tabular">{user.times_won || 0}</span> wins</span>
                    {user.referred_by_name && <span className="ml-auto truncate text-[#10b981]">via {user.referred_by_name}</span>}
                  </div>
                </Card>
              ))}
              {filteredUsers.length === 0 && (
                <EmptyState icon={Users} title={searchQuery ? 'No matches' : 'No users yet'} message={searchQuery ? 'Try a different name or number.' : 'Members will appear here once they sign up.'} />
              )}
            </div>

            {/* Desktop: table */}
            <Card className="hidden overflow-hidden md:block">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/[0.03]">
                    <tr>
                      {['Name', 'Nickname', 'WhatsApp', 'Referred By'].map((h) => (
                        <th key={h} className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#a1a1aa]">{h}</th>
                      ))}
                      <SortHeader col="tickets" label="Tickets" color="#A855F7" />
                      <SortHeader col="wins" label="Wins" color="#EAB308" />
                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[#a1a1aa]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.06]">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="transition-colors hover:bg-white/[0.02]">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium text-white">{user.name}</div>
                            {isRecent(user) && <NewBadge user={user} />}
                            {user.possible_duplicate && <DupBadge user={user} />}
                          </div>
                          <div className="text-xs text-[#52525b]">{new Date(user.created_at).toLocaleDateString()}</div>
                        </td>
                        <td className="px-5 py-4">{renderNickname(user)}</td>
                        <td className="px-5 py-4">{renderNumbers(user)}</td>
                        <td className="px-5 py-4">
                          {user.referred_by_name ? <span className="text-sm text-[#10b981]">{user.referred_by_name}</span> : <span className="text-sm text-[#52525b]">-</span>}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2"><Ticket className="h-4 w-4 text-[#A855F7]" /><span className="font-semibold text-white tabular">{user.total_tickets || 0}</span></div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2"><Trophy className="h-4 w-4 text-[#EAB308]" /><span className="font-semibold text-white tabular">{user.times_won || 0}</span></div>
                        </td>
                        <td className="px-5 py-4">{renderUserActions(user)}</td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center text-[#52525b]">
                          {searchQuery ? 'No users found matching your search' : 'No users yet'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'invite' && (
          <div className="mx-auto max-w-xl space-y-4" data-testid="invite-content">
            <Card className="overflow-hidden">
              <div className="flex items-center gap-3 border-b border-white/[0.06] px-5 py-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#A855F7]/15">
                  <Link2 className="h-4 w-4 text-[#A855F7]" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="font-heading text-[15px] font-semibold text-white">Invite link</h2>
                  <p className="text-xs text-[#a1a1aa]">Send this to new members — it opens your sign-up page</p>
                </div>
              </div>
              <div className="space-y-4 p-5">
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[#a1a1aa]">Your link</label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      readOnly
                      value={inviteUrl}
                      data-testid="invite-link-input"
                      onFocus={(e) => e.target.select()}
                      className="h-12 flex-1 rounded-xl border border-white/[0.08] bg-[#09090b] px-4 text-sm text-white focus:border-[#A855F7] focus:outline-none"
                    />
                    <Button
                      onClick={() => copyInvite(inviteUrl, 'full')}
                      data-testid="copy-invite-link-btn"
                      className="h-12 rounded-full bg-white/[0.08] px-5 font-semibold text-white hover:bg-white/[0.14]"
                    >
                      {copiedInvite === 'full' ? <Check className="mr-1.5 h-4 w-4 text-[#10b981]" /> : <Copy className="mr-1.5 h-4 w-4" />}
                      {copiedInvite === 'full' ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>

                <Button
                  onClick={() => shareInviteWhatsApp(inviteUrl)}
                  data-testid="share-invite-whatsapp-btn"
                  className="h-12 w-full rounded-full bg-[#25D366] font-semibold text-black hover:bg-[#20bd5a]"
                >
                  <MessageCircle className="mr-2 h-5 w-5" /> Share on WhatsApp
                </Button>

                <div className="flex flex-col items-center gap-3 rounded-xl border border-white/[0.06] bg-white p-4">
                  <div className="rounded-lg bg-white p-1" data-testid="invite-qr">
                    <QRCodeSVG value={inviteUrl} size={168} level="M" />
                  </div>
                  <p className="text-center text-xs font-medium text-[#52525b]">Members can scan this to open the sign-up page</p>
                </div>

                <p className="text-xs leading-relaxed text-[#71717a]">
                  Tip: members still need the club access code to finish signing up. Share the code separately so only people you trust can join.
                </p>
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'menus' && (
          <div data-testid="menus-content">
            <MenusManager />
          </div>
        )}

        {activeTab === 'polls' && (
          <div data-testid="polls-content">
            <PollsManager onFeedbackSeen={fetchFeedbackCount} />
          </div>
        )}

        {activeTab === 'scanlog' && (
          <div data-testid="scanlog-content">
            <ScanHistory />
          </div>
        )}
      </AppShell>

      {showAdminScanner && <QRScannerModal onClose={() => setShowAdminScanner(false)} />}

      {referralUser && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={() => setReferralUser(null)}>
          <div className="w-full max-w-md rounded-t-3xl bg-[#0f0f11] p-5 sm:rounded-3xl" onClick={(e) => e.stopPropagation()} data-testid="referral-modal">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold text-white">Referral · {referralUser.name}</h3>
              <button onClick={() => setReferralUser(null)} className="text-[#a1a1aa] hover:text-white"><X className="h-5 w-5" /></button>
            </div>

            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[#a1a1aa]">Introduced by</label>
            <input value={refSearch} onChange={(e) => setRefSearch(e.target.value)} placeholder="Search members…" data-testid="referrer-search"
              className="mb-2 h-10 w-full rounded-lg border border-white/[0.08] bg-[#09090b] px-3 text-sm text-white placeholder:text-[#52525b] focus:border-[#10b981] focus:outline-none" />
            <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border border-white/[0.06] p-1">
              <button onClick={() => setReferrerChoice('')} data-testid="referrer-none"
                className={`flex w-full items-center rounded-md px-3 py-2 text-left text-sm ${!referrerChoice ? 'bg-[#10b981]/20 text-white' : 'text-[#a1a1aa] hover:bg-white/[0.04]'}`}>
                No one (clear)
              </button>
              {users.filter((u) => u.id !== referralUser.id && ((u.name || '').toLowerCase().includes(refSearch.toLowerCase()) || (u.nickname || '').toLowerCase().includes(refSearch.toLowerCase()) || (u.whatsapp_number || '').includes(refSearch))).slice(0, 40).map((u) => (
                <button key={u.id} onClick={() => setReferrerChoice(u.id)} data-testid={`referrer-opt-${u.id}`}
                  className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm ${referrerChoice === u.id ? 'bg-[#10b981]/20 text-white' : 'text-[#a1a1aa] hover:bg-white/[0.04]'}`}>
                  <span className="flex min-w-0 items-center gap-1.5 truncate">
                    <span className="truncate">{u.name}</span>
                    {u.nickname && <span className="shrink-0 rounded-full bg-[#A855F7]/20 px-1.5 py-0.5 text-[10px] font-medium text-[#c084fc]">“{u.nickname}”</span>}
                  </span>
                  <span className="ml-2 shrink-0 text-xs text-[#52525b]">{u.whatsapp_number}</span>
                </button>
              ))}
            </div>

            {/* Friends this member referred — adjust each friend's bubbles directly */}
            {(() => {
              const friends = users.filter((u) => u.referred_by === referralUser.id);
              return (
                <div className="mt-5 border-t border-white/[0.08] pt-4" data-testid="referred-friends-section">
                  <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-[#a1a1aa]">
                    Friends they referred ({friends.length})
                  </label>
                  {friends.length === 0 ? (
                    <p className="text-xs text-[#52525b]">No referred friends yet. Set someone’s “Introduced by” to {referralUser.name} to see them here.</p>
                  ) : (
                    <div className="max-h-60 space-y-2 overflow-y-auto pr-1">
                      {friends.map((f) => (
                        <div key={f.id} className="rounded-xl bg-white/[0.03] p-3" data-testid={`referred-friend-${f.id}`}>
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <span className="flex min-w-0 items-center gap-1.5 truncate">
                              <span className="truncate text-sm font-medium text-white">{f.name}</span>
                              {f.nickname && <span className="shrink-0 rounded-full bg-[#A855F7]/20 px-1.5 py-0.5 text-[10px] font-medium text-[#c084fc]">“{f.nickname}”</span>}
                            </span>
                            <span className="shrink-0 text-xs font-semibold text-[#22c55e]">{f.referral_bubbles ?? 0}/5</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {[0, 1, 2, 3, 4, 5].map((n) => (
                              <button
                                key={n}
                                disabled={savingFriendId === f.id}
                                onClick={() => setFriendBubbles(f.id, n)}
                                data-testid={`friend-bubble-${f.id}-${n}`}
                                className={`h-8 w-8 rounded-full text-xs font-semibold transition-colors disabled:opacity-50 ${
                                  (f.referral_bubbles ?? 0) === n
                                    ? 'bg-[#22c55e] text-black'
                                    : 'bg-white/[0.06] text-[#a1a1aa] hover:bg-white/[0.12]'
                                }`}
                              >
                                {n}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            <Button onClick={saveReferral} disabled={savingReferral} data-testid="save-referral-btn"
              className="mt-4 h-12 w-full rounded-full bg-[#10b981] font-semibold text-black hover:bg-[#0ea371]">
              {savingReferral ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}

      {mergeUser && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={() => setMergeUser(null)}>
          <div className="w-full max-w-md rounded-t-3xl bg-[#0f0f11] p-5 sm:rounded-3xl" onClick={(e) => e.stopPropagation()} data-testid="merge-modal">
            <div className="mb-1 flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold text-white">Merge duplicate</h3>
              <button onClick={() => setMergeUser(null)} className="text-[#a1a1aa] hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <p className="mb-3 text-sm text-[#a1a1aa]">Keep <span className="font-semibold text-[#A855F7]">{mergeUser.name}</span> and merge another account into it. The duplicate’s tickets, numbers and referrals move over, then it’s removed.</p>
            <input value={mergeSearch} onChange={(e) => setMergeSearch(e.target.value)} placeholder="Search the duplicate account…" data-testid="merge-search"
              className="mb-2 h-10 w-full rounded-lg border border-white/[0.08] bg-[#09090b] px-3 text-sm text-white placeholder:text-[#52525b] focus:border-[#A855F7] focus:outline-none" />
            <div className="max-h-52 space-y-1 overflow-y-auto rounded-lg border border-white/[0.06] p-1">
              {users.filter((u) => u.id !== mergeUser.id && ((u.name || '').toLowerCase().includes(mergeSearch.toLowerCase()) || (u.whatsapp_number || '').includes(mergeSearch))).slice(0, 40).map((u) => (
                <button key={u.id} onClick={() => setMergeDuplicateId(u.id)} data-testid={`merge-opt-${u.id}`}
                  className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm ${mergeDuplicateId === u.id ? 'bg-[#A855F7]/25 text-white' : 'text-[#a1a1aa] hover:bg-white/[0.04]'}`}>
                  <span className="truncate">{u.name}</span>
                  <span className="ml-2 shrink-0 text-xs text-[#52525b]">{u.whatsapp_number} · {u.total_tickets || 0}t</span>
                </button>
              ))}
            </div>
            <Button onClick={doMerge} disabled={merging || !mergeDuplicateId} data-testid="confirm-merge-btn"
              className="mt-4 h-12 w-full rounded-full bg-[#A855F7] font-semibold text-white hover:bg-[#9333ea] disabled:opacity-40">
              {merging ? 'Merging…' : 'Merge into ' + mergeUser.name}
            </Button>
          </div>
        </div>
      )}

      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => { setShowDeleteDialog(false); setUserToDelete(null); }}
        onConfirm={deleteUser}
        loading={deleting}
        title="Delete User?"
        message={`Are you sure you want to delete "${userToDelete?.name}"? The user will be removed from the active list but their ticket history and metrics will be preserved for business intelligence. They will not be able to log in anymore.`}
      />

      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 36 }}
            className="w-full max-w-md rounded-t-3xl border border-white/[0.08] bg-[#18181b] p-6 pb-safe sm:rounded-3xl"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#EAB308]/15">
                <KeyRound className="h-6 w-6 text-[#EAB308]" />
              </div>
              <div className="min-w-0">
                <h3 className="font-heading text-lg font-semibold text-white">Reset password</h3>
                <p className="truncate text-sm text-[#a1a1aa]">for <span className="font-medium text-white">{resetPasswordUser?.name}</span></p>
              </div>
            </div>

            <div className="mt-5 rounded-xl bg-[#09090b] p-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[#71717a]">Temporary password</div>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 break-all font-mono text-lg text-[#EAB308]">{generatedPassword}</code>
                <Button
                  data-testid="copy-password-btn"
                  onClick={copyPassword}
                  variant="outline"
                  size="sm"
                  className={`h-10 w-10 shrink-0 rounded-full p-0 ${copiedPassword ? 'border-[#10b981] text-[#10b981]' : 'border-white/[0.08] text-[#a1a1aa]'} hover:text-white`}
                >
                  {copiedPassword ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="mt-3 rounded-xl border border-[#EAB308]/25 bg-[#EAB308]/10 px-3.5 py-2.5">
              <p className="text-sm text-[#EAB308]">Share this password with the member manually. They'll be asked to change it after logging in.</p>
            </div>

            <div className="mt-5 flex gap-3">
              <Button
                data-testid="cancel-reset-btn"
                onClick={() => { setShowResetModal(false); setResetPasswordUser(null); setGeneratedPassword(''); }}
                variant="outline"
                className="h-12 flex-1 rounded-full border-white/[0.08] text-[#a1a1aa] hover:text-white"
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-reset-btn"
                onClick={handleResetPassword}
                disabled={resettingPassword}
                className="h-12 flex-1 rounded-full bg-[#EAB308] font-semibold text-black hover:bg-[#CA8A04]"
              >
                {resettingPassword ? 'Resetting…' : 'Reset password'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default AdminDashboard;
